import { cleanXeroText } from '../clean-xero-text';

/** Whether the send's own reserved Xero calls are used up. UNKNOWN: it could not be read. */
export type SendAllowance = 'SPENT' | 'AVAILABLE' | 'UNKNOWN';

const RESUME_CONTROL: Record<string, string> = {
  SEND: 'Resume sending',
  BUILD: 'Resume this build',
  PREPARE: 'Resume Xero preparation',
};

/** Acknowledging is open to any officer; only the officer who started the build can wake it. */
export const heldAcknowledgedResumeMessage = (ownerName?: string | null) =>
  `Held items acknowledged; held items remain unpaid for follow-up. If the build shows as stopped, ${ownerName?.trim() || 'the officer who started this build'} can choose Resume this build.`;

export const SEND_NO_PROGRESS_MESSAGE =
  'Sending paused because no payslip moved forward over several tries. Xero may be slow, or this run may have used its reserved Xero calls. Nothing unsent was written. Choose Resume sending once. If it stops again, contact Statura support.';
/** Words of every changed-draft line, so a page can tell one is showing. */
export const DRAFT_CHANGED_WORDS = 'changed after this plan was approved';
export const SEND_NO_PROGRESS_DRAFT_CHANGED =
  'Sending paused because no payslip moved forward over several tries. Nothing unsent was written. A Xero draft changed after this plan was approved: check the drafts listed on this page, then contact Statura support.';
const SEND_NO_PROGRESS_AVAILABLE =
  'Sending paused because no payslip moved forward over several tries. Xero may be slow. Nothing unsent was written. Choose Resume sending to try again. If it stops again, contact Statura support.';
// No control from an approved run leads to a new version: Replace needs an unapproved run, and a
// cancelled run keeps the period, so a new run for it is refused. Only support can move this on.
const SEND_ALLOWANCE_SPENT =
  'Resume sending won’t help: this send has used its allowance. Contact Statura support.';
const XERO_CHECK_REQUIRED_MESSAGE =
  'Sending has stopped because the Xero check needs review. Some payslips may already be in Xero, including one whose send was refused or whose answer was lost. Read the Xero check result on this page and check Xero before doing anything. Do not send again.';
const OTHER_STOP_MESSAGE = 'Contact Statura support.';
const HOLDS_REVIEW_MESSAGE =
  'Some items are held and need your review before this build can finish. Review the held items on this page and acknowledge them. The build then carries on, and held items stay unpaid.';
const HOLDS_ACKNOWLEDGED_MESSAGE =
  'Held items are acknowledged. Choose Resume this build to finish it. Nothing already done is lost.';
const BUILD_OFFICER_MESSAGES: Record<string, string> = {
  payroll_build_authorisation_expired:
    'This build’s approval to run has expired. Choose Reauthorise build to confirm it again. Nothing already done is lost.',
  payroll_build_authorisation_invalid:
    'This build’s approval to run is no longer valid. Contact Statura support.',
  payroll_build_pay_run_not_buildable:
    'This pay run can’t be built in its current state. Contact Statura support.',
  payroll_build_run_not_building:
    'This pay run is no longer being built. Refresh the page. If it stays like this, contact Statura support.',
};
export const SEND_ALLOWANCE_EXPIRED_MESSAGE =
  'Sending stopped because its Xero allowance has expired. Some payslips may already be in Xero. Do not send again. Contact Statura support.';
const NOT_XERO_WHOLE_RUN_MESSAGE =
  'None of these payslips was sent. Fix the cause shown on this page, stop this send, choose Update Xero before sending, approve the updated plan if asked, and send again. No payslip was sent. Any leave already sent to Xero stays there and may show on the draft.';
export const WHOLE_RUN_REFUSED_MESSAGE =
  'Xero refused these payslips. Fix the cause, stop this send, choose Update Xero before sending, approve the updated plan if asked, and send again. No payslip was sent. Any leave already sent to Xero stays there and may show on the draft.';
/** Says "Xero refused" only when every refusal is known to be Xero's own answer. */
export const wholeRunRefusedMessage = (refusedByXero: boolean | undefined) =>
  refusedByXero ? WHOLE_RUN_REFUSED_MESSAGE : NOT_XERO_WHOLE_RUN_MESSAGE;

/** Why a payslip was refused. XERO: Xero's own answer. The rest are Statura's checks before any write. */
export type RejectionCause =
  | 'XERO'
  | 'DRAFT_EARNINGS'
  | 'DRAFT_CHANGED'
  | 'DRAFT_UNCARRYABLE'
  /** Statura's own pre-send check, already in plain words: those words are shown. */
  | 'WORDED_CHECK'
  | 'OTHER';
/** Words for a refusal Xero didn't make. `who` is a name, or a reference the page turns into one. */
/** What the runtime puts in place of a Xero message that carried a credential: not Xero's words. */
export const XERO_TEXT_WITHHELD = 'Xero rejected this payslip. Review the draft in Xero.';

/** The connector's own words for an empty 4xx: not Xero's either. */
const CONNECTOR_FALLBACK_TEXT = 'Xero rejected the payload';

/** Xero's words, safe to show as text. The only route for them to the page; null if nothing is left.
 * Never for Sentry or logs. */
export function xeroSaidLine(text: string | undefined): string | null {
  if (text === XERO_TEXT_WITHHELD || text === CONNECTOR_FALLBACK_TEXT) return null;
  const clean = cleanXeroText(text);
  return clean ? `Xero said: ${clean}` : null;
}

export function rejectionCauseLine(
  who: string,
  cause: RejectionCause,
  { checkInXero = true } = {},
): string | null {
  if (cause === 'XERO') return `${who}: Xero refused this payslip.`;
  const why =
    cause === 'DRAFT_EARNINGS'
      ? `${who}’s Xero draft already has an earnings amount Statura didn’t put there, so this payslip wasn’t sent.`
      : cause === 'DRAFT_UNCARRYABLE'
        ? `${who}’s Xero draft has a line Statura can’t keep safely, so this payslip wasn’t sent.`
        : cause === 'DRAFT_CHANGED'
          ? `${who}’s Xero draft ${DRAFT_CHANGED_WORDS}, so this payslip wasn’t sent.`
          : cause === 'OTHER'
            ? `${who}’s payslip wasn’t sent because Statura couldn’t safely write it to the Xero draft.`
            : null;
  if (!why) return null;
  return checkInXero ? `${why} Check it in Xero.` : why;
}

const UNCONFIRMED_STOP_MESSAGE =
  'Sending has stopped. Check each payslip listed on this page in Xero before posting or paying any adjustment.';
const EXHAUSTED_STOP_MESSAGE =
  'Sending has stopped. The payslips listed on this page were not sent.';
const PLAN_NOT_APPROVED_MESSAGE =
  'Sending stopped because this Xero plan isn’t approved for sending, and it can’t be approved again once sending has started. Contact Statura support.';
const SEND_STEP_MESSAGE =
  'Sending stopped before it finished. Choose Resume sending to continue from saved progress. Nothing already sent is sent again. If it stops again, contact Statura support.';
/** A stall or outage on a send, never a decision: another tick repeats every check, and uncertain
 * attempts are read back, not sent. Anything not listed fails closed. `unexpected` is what a failed
 * fetch becomes: backgroundFailureCode finds no code in its message. */
const SEND_TRANSIENT_CODES: readonly string[] = [
  'unexpected',
  'payroll_build_deadline',
  'payroll_statement_timeout',
  'payroll_background_unavailable',
  'payroll_provider_evidence_unavailable',
  'payroll_provider_attempts_unavailable',
  'payroll_provider_state_unavailable',
  'payroll_provider_quota_unavailable',
  'payroll_provider_context_unavailable',
  'payroll_provider_ledger_unavailable',
  'payroll_provider_readback_unavailable',
  'payroll_provider_readback_not_recorded',
  'payroll_provider_acceptance_not_recorded',
  'payroll_leave_readback_unavailable',
  'payroll_leave_evidence_unavailable',
  'payroll_leave_progress_unavailable',
  'payroll_leave_draft_unavailable',
  'payroll_rebuild_mode_unavailable',
  'payroll_rebuild_leave_paused',
];
const DRAFT_CHANGED_MESSAGE =
  'Sending stopped because a Xero draft changed after this plan was approved. Those payslips were not sent. Check those drafts in Xero, then contact Statura support.';
const STEP_STOP_MESSAGE: Record<string, string> = {
  BUILD:
    'The build stopped before it finished. Choose Resume this build to continue from saved progress. If it stops again, contact Statura support.',
  PREPARE:
    'Xero preparation stopped before it finished. Choose Resume Xero preparation to continue from saved progress. If it stops again, contact Statura support.',
};

const NEW_VERSION_MESSAGES: Record<string, string> = {
  payroll_capture_config_changed:
    'A pay rate, rule, holiday or organisation setting changed during capture. Choose Build a new version.',
  payroll_capture_source_no_longer_capturable:
    'A timesheet or leave record changed during capture and can no longer be read. Choose Build a new version.',
  payroll_capture_requires_new_build:
    'This build started before the latest payroll update. Choose Build a new version.',
};
const SEND_MESSAGES: Record<string, string> = {
  payroll_send_no_progress: SEND_NO_PROGRESS_MESSAGE,
  payroll_leave_pass_window_exceeded:
    'The leave check took too long to finish, so nothing further was sent. Choose Resume sending. If it stops again, contact Statura support.',
  payroll_send_worker_held:
    'Only workers on hold remain. Review the holds listed on this page. When they are fixed, choose Resume sending.',
};

export const STOPPED_COMMAND_STATES: readonly string[] = [
  'PAUSED',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
];

type StoppedTask = {
  operation?: string;
  state?: string;
  failureCode?: string | null;
  reason?: string | null;
};
interface StopFacts {
  allowance?: SendAllowance;
  /** Build a new version is on offer. Undefined: not known here, so the button decides. */
  canReplace?: boolean;
  /** False until the database guard is live: Resume is not offered, so it is not mentioned. */
  resumeAvailable?: boolean;
  /** A changed-draft line is showing: Resume once is not the next step. */
  draftChanged?: boolean;
  /** A worker's latest attempt, not rejected, sits under another plan. */
  earlierPlanAttempt?: boolean;
  /** Why a Xero check stopped the send, when refusals alone explain it. */
  refusedStop?: RefusedStop;
  /** Every refusal is known to be Xero's own answer. Otherwise no words blame Xero. */
  refusedByXero?: boolean;
  /** A listed worker's refusal has no record. */
  anyUnconfirmed?: boolean;
  /** Held items still need acknowledging. Undefined: not known here. */
  holdsPending?: boolean;
}

const isStep = (task: StoppedTask) => !task.reason || task.reason === 'STEP_UNAVAILABLE';

/** A Xero check that found sending had started, at its own tick. Nothing was read from Xero. */
export const PREPARE_AFTER_SEND_STARTED = 'payroll_prepare_after_send_started';

/** The one map from a stopped command to officer words. Never returns a raw code. A code is only
 * trusted on a step stop: it can be left over from an earlier pause that was resumed. */
export function backgroundStopMessage(task: StoppedTask, facts: StopFacts = {}): string {
  if (facts.refusedStop === 'UNCONFIRMED' && !facts.earlierPlanAttempt)
    return UNCONFIRMED_REFUSAL_MESSAGE;
  if (facts.refusedStop === 'WHOLE_RUN' && !facts.earlierPlanAttempt && task.state !== 'CANCELLED')
    return wholeRunRefusedMessage(facts.refusedByXero);
  if (task.reason === 'XERO_CHECK_REQUIRED')
    return facts.earlierPlanAttempt
      ? 'Nothing further will send. Don’t re-approve or press send again. Contact Statura support.'
      : facts.refusedStop === 'WHOLE_RUN'
        ? wholeRunRefusedMessage(facts.refusedByXero)
        : facts.refusedStop === 'PART_OF_RUN'
          ? facts.anyUnconfirmed
            ? UNCONFIRMED_STOP_MESSAGE
            : EXHAUSTED_STOP_MESSAGE
          : XERO_CHECK_REQUIRED_MESSAGE;
  if (!isStep(task)) return OTHER_STOP_MESSAGE;
  if (facts.resumeAvailable === false && canResumeStoppedTask(task, facts))
    return OTHER_STOP_MESSAGE;
  const code = task.failureCode ?? '';
  if (code === 'payroll_build_holds_acknowledgement_required')
    return facts.holdsPending === false ? HOLDS_ACKNOWLEDGED_MESSAGE : HOLDS_REVIEW_MESSAGE;
  if (Object.hasOwn(BUILD_OFFICER_MESSAGES, code)) return BUILD_OFFICER_MESSAGES[code]!;
  if (code === PREPARE_AFTER_SEND_STARTED) return UPDATE_AFTER_SEND_STARTED_MESSAGE;
  if (code === 'payroll_send_draft_changed') return DRAFT_CHANGED_MESSAGE;
  if (code === 'payroll_send_allowance_expired') return SEND_ALLOWANCE_EXPIRED_MESSAGE;
  if (code === 'payroll_provider_plan_approval_required') return PLAN_NOT_APPROVED_MESSAGE;
  if (task.operation === 'SEND' && SEND_TRANSIENT_CODES.includes(code)) {
    return facts.resumeAvailable === false ? OTHER_STOP_MESSAGE : SEND_STEP_MESSAGE;
  }
  if (Object.hasOwn(NEW_VERSION_MESSAGES, code))
    return facts.canReplace === false ? OTHER_STOP_MESSAGE : NEW_VERSION_MESSAGES[code]!;
  if (code === 'payroll_send_no_progress') {
    if (facts.allowance === 'SPENT') return SEND_ALLOWANCE_SPENT;
    if (facts.draftChanged) return SEND_NO_PROGRESS_DRAFT_CHANGED;
    if (facts.allowance === 'AVAILABLE') return SEND_NO_PROGRESS_AVAILABLE;
  }
  if (
    Object.hasOwn(SEND_MESSAGES, code) &&
    task.operation !== 'BUILD' &&
    task.operation !== 'PREPARE'
  )
    return SEND_MESSAGES[code]!;
  if (canResumeStoppedTask(task, facts) && task.operation)
    return STEP_STOP_MESSAGE[task.operation] ?? OTHER_STOP_MESSAGE;
  return OTHER_STOP_MESSAGE;
}

/** A paused step may be resumed only where a retry can't duplicate anything. Mirrors, and never
 * widens, the server's reason check. */
export function canResumeStoppedTask(task: StoppedTask, facts: StopFacts = {}): boolean {
  if (task.state !== 'PAUSED' || task.reason !== 'STEP_UNAVAILABLE') return false;
  if (Object.hasOwn(BUILD_OFFICER_MESSAGES, task.failureCode ?? '')) return false;
  if (task.failureCode === 'payroll_build_holds_acknowledgement_required' && facts.holdsPending)
    return false;
  if (
    (facts.refusedStop === 'WHOLE_RUN' || facts.refusedStop === 'UNCONFIRMED') &&
    task.operation === 'SEND'
  )
    return false;
  const code = task.failureCode ?? '';
  if (task.operation === 'SEND')
    return (
      SEND_TRANSIENT_CODES.includes(code) ||
      (Object.hasOwn(SEND_MESSAGES, code) &&
        !(code === 'payroll_send_no_progress' && facts.allowance === 'SPENT'))
    );
  if (code === PREPARE_AFTER_SEND_STARTED) return false;
  if (task.operation === 'BUILD' || task.operation === 'PREPARE')
    return !Object.hasOwn(NEW_VERSION_MESSAGES, code);
  return false;
}

/** Only the task's owner can resume it, so a colleague is told who to ask. */
export function stoppedTaskOwnerNextStep(
  operation: string,
  control: string | null,
  ownerName?: string | null,
) {
  const who =
    ownerName?.trim() ||
    (operation === 'BUILD'
      ? 'the officer who started this build'
      : 'the officer who started this step');
  return `This has stopped and only ${who} can continue it. Ask them to open this pay run and choose ${control ?? 'Resume'}.`;
}

/** Get Xero ready found its check not running: a replayed or paused task, never a new one. */
export function xeroCheckStoppedMessage(task: StoppedTask) {
  const control = resumeControlName(task);
  return task.state === 'PAUSED' && control && canResumeStoppedTask(task)
    ? `Checking Xero has stopped. No payslips were sent. ${stoppedTaskOwnerNextStep('PREPARE', control)}`
    : 'Checking Xero has stopped and choosing Get Xero ready can’t restart it. No payslips were sent. Contact Statura support.';
}

export function resumeControlName(task: StoppedTask): string | null {
  return RESUME_CONTROL[task.operation ?? ''] ?? null;
}

/** Every call on a reservation is refused once consumed reaches allocated, and the same key is
 * never replenished. An expired row or a failed read proves nothing either way. */
export function sendAllowanceFromReservation(
  row: { allocated: number; consumed: number; expires_at: string } | null | undefined,
  now: number,
): SendAllowance {
  if (!row || !(Date.parse(row.expires_at) > now)) return 'UNKNOWN';
  return row.consumed >= row.allocated ? 'SPENT' : 'AVAILABLE';
}

/** Newest task of each kind that has stopped. An officer's own cancel says nothing further. */
export function stoppedSendTasks<
  T extends { operation: string; state: string; createdAt: string; reason?: string | null },
>(items: readonly T[]): T[] {
  const newest = new Map<string, T>();
  for (const item of items) {
    const held = newest.get(item.operation);
    if (!held || Date.parse(item.createdAt) > Date.parse(held.createdAt))
      newest.set(item.operation, item);
  }
  return [...newest.values()].filter(
    (item) => STOPPED_COMMAND_STATES.includes(item.state) && item.reason !== 'CANCELLED',
  );
}

/** UNCONFIRMED: all refused, but at least one has no record of Xero's answer. */
export type RefusedStop = 'WHOLE_RUN' | 'PART_OF_RUN' | 'UNCONFIRMED';

type AttemptFacts = {
  workerId: string;
  planDigest: string | null;
  state: string;
  attemptNo: number;
  updatedAt?: string;
};
function latestAttempts(attempts: readonly AttemptFacts[]) {
  const latest = new Map<string, AttemptFacts>();
  for (const a of attempts)
    if ((latest.get(a.workerId)?.attemptNo ?? 0) < a.attemptNo) latest.set(a.workerId, a);
  return [...latest.values()];
}

/** A Xero check that refusals alone explain. Once everyone else is done the run stops there at any
 * rejection count, with no retry to come. Any other unsettled attempt keeps the usual warning. */
export function refusedStopKind(attempts: readonly AttemptFacts[]): {
  kind: RefusedStop | undefined;
  refused: string[];
} {
  const latest = latestAttempts(attempts);
  const refused = latest
    .filter((a) => a.state === 'REJECTED')
    .map((a) => a.workerId)
    .sort();
  if (!refused.length || latest.some((a) => !['REJECTED', 'VERIFIED'].includes(a.state)))
    return { kind: undefined, refused: [] };
  return { kind: refused.length === latest.length ? 'WHOLE_RUN' : 'PART_OF_RUN', refused };
}

/** One number for the send and the page, so what the page says can't drift from what the send does. */
export const MAX_REJECTED_ATTEMPTS = 3;
const MANY_REFUSED = 3;
export const MANY_REFUSED_MESSAGE =
  'Many payslips were refused. Don’t change the Xero draft. Contact Statura support.';

/** A refusal counts toward a worker's limit only if it came during this send's command, as in the
 * database's rule. A Resume keeps the command, so its refusals still count. Not knowing either
 * time counts it: never a retry the database would refuse. */
export function refusalCounts(updatedAt: string | undefined, sendStartedAt: string | undefined) {
  const at = Date.parse(updatedAt ?? '');
  const since = Date.parse(sendStartedAt ?? '');
  return Number.isNaN(at) || Number.isNaN(since) || at >= since;
}

type RefusalRecords = Readonly<
  Record<string, { planDigest: string; attemptNo?: number } | undefined>
>;
const UNCONFIRMED_LINE_END =
  'This payslip could not be confirmed. Check it in Xero before posting or paying any adjustment.';
const unconfirmedLine = (workerId: string) => `${workerId}: ${UNCONFIRMED_LINE_END}`;

/** Lines for workers Xero refused for good, by worker reference for the page to name. Read from the
 * attempts alone: a live plan can move, or turn the worker HOLD, and they would still be unsent. A
 * later attempt that wasn't rejected ends it. True at the attempt limit, or at any count once
 * `allDecided` says every worker to send has an outcome, because the run then stops with no retry.
 * A whole run refused has its own message. One such worker doesn't delay the others. */
export function exhaustedAttemptLines(
  attempts: readonly AttemptFacts[],
  {
    allDecided = false,
    maxRejected = MAX_REJECTED_ATTEMPTS,
    sendStartedAt,
    sendUnknown = false,
    records,
  }: {
    allDecided?: boolean;
    maxRejected?: number;
    sendStartedAt?: string;
    /** The send's task couldn't be read, so "not sent for good" can't be claimed. */
    sendUnknown?: boolean;
    /** Refusal records. Given: a worker with none is unconfirmed, not refused. */
    records?: RefusalRecords;
  } = {},
): string[] {
  const latest = latestAttempts(attempts);
  const othersSent = latest.some((a) => a.state === 'VERIFIED');
  const unconfirmed = (workerId: string) => {
    if (!records) return false;
    const attempt = latest.find((a) => a.workerId === workerId);
    const record = records[workerId];
    return record?.planDigest !== attempt?.planDigest || record?.attemptNo !== attempt?.attemptNo;
  };
  // Every latest attempt refused is the recovery case, with its own message and steps. Anyone
  // without a record still needs telling.
  if (latest.every((a) => a.state === 'REJECTED'))
    return latest
      .map((a) => a.workerId)
      .filter(unconfirmed)
      .sort()
      .map(unconfirmedLine);
  const lines = latest
    .filter((a) => a.state === 'REJECTED')
    .map((a) => a.workerId)
    .sort()
    .flatMap((workerId) => {
      const perPlan = new Map<string | null, number>();
      for (const a of attempts)
        if (
          a.workerId === workerId &&
          a.state === 'REJECTED' &&
          refusalCounts(a.updatedAt, sendStartedAt)
        )
          perPlan.set(a.planDigest, (perPlan.get(a.planDigest) ?? 0) + 1);
      if (!(allDecided && othersSent) && Math.max(0, ...perPlan.values()) < maxRejected) return [];
      if (sendUnknown)
        return [
          `${workerId}: This payslip was not sent. Refresh this page to see what to do next.`,
        ];
      if (unconfirmed(workerId)) return [unconfirmedLine(workerId)];
      return [
        `${workerId}: This payslip was not sent. Remove it from the Xero draft before posting. They will be paid by adjustment. Contact Statura support: this pay run stays open.`,
      ];
    });
  // Many at once points past one payslip's draft: nobody should edit drafts on this page's say-so.
  const explicit = lines.filter((line) => !line.endsWith(UNCONFIRMED_LINE_END));
  if (!sendUnknown && othersSent && explicit.length > MANY_REFUSED) {
    const workers = explicit.map((line) => line.slice(0, line.indexOf(':')));
    return [
      MANY_REFUSED_MESSAGE,
      ...workers.map((id) => `${id}: This payslip was not sent.`),
      ...lines.filter((line) => line.endsWith(UNCONFIRMED_LINE_END)),
    ];
  }
  return lines;
}

/** The one test for "Xero refused the whole send": every worker's latest attempt was refused, and
 * each has its record, made when Xero answered and a read showed nothing changed, or before any
 * POST. An attempt settled as absent after the window has none: it could have landed. No records,
 * no recovery. Matches the database's approval rule, and is stricter. */
export const wholeSendRefused = (
  attempts: readonly AttemptFacts[],
  records: RefusalRecords | null | undefined,
): boolean =>
  refusedStopKind(attempts).kind === 'WHOLE_RUN' &&
  !!records &&
  latestAttempts(attempts).every((attempt) => {
    const record = records[attempt.workerId];
    return record?.planDigest === attempt.planDigest && record.attemptNo === attempt.attemptNo;
  });

export const UNCONFIRMED_REFUSAL_MESSAGE =
  'One or more payslips couldn’t be confirmed, so this plan can’t be changed. Contact Statura support.';

/** Display only: the same test the connector uses for a line it refuses without a review. */
/** A worker's review can't get their payslip sent once the Xero check has stopped the send. */
export const EARNINGS_REVIEW_TOO_LATE_MESSAGE =
  'Sending has stopped, so this review can’t get the payslip sent. This payslip was not sent. Remove it from the Xero draft before posting. They will be paid by adjustment. Contact Statura support: this pay run stays open.';

export const UPDATE_AFTER_SEND_STARTED_MESSAGE =
  'Sending has started. Statura could not finish the check, so do not send again. Review the saved result and check the named payslips in Xero; keep this pay run open while the outcome is confirmed.';

/** Latest attempts a POST can't have left behind. Anything else means sending has started. */
const NOT_POSTED: readonly string[] = ['CLAIMED_NOT_SENT', 'BASELINE_SAVED', 'REJECTED'];
const NOT_SETTLED: readonly string[] = ['SEND_STARTED', 'OUTCOME_UNKNOWN', 'MISMATCH'];

/** Update can't change a plan once a payslip may have reached Xero. Null: no POST can have happened,
 * so a wholly refused send, where Update is the way forward, is never refused here. */
export function updateAfterSendStarted(input: {
  attempts: readonly { workerId: string; attemptNo: number; state: string }[];
  /** Workers the send task covers, when known. More than have attempts means some weren't tried. */
  taskTotal?: number;
  sendActive?: boolean;
  names: ReadonlyMap<string, string>;
}): string | null {
  const latest = new Map<string, { attemptNo: number; state: string }>();
  for (const attempt of input.attempts) {
    const prior = latest.get(attempt.workerId);
    if (!prior || attempt.attemptNo > prior.attemptNo) latest.set(attempt.workerId, attempt);
  }
  const rows = [...latest].map(([workerId, attempt]) => ({ workerId, state: attempt.state }));
  if (!rows.some((row) => !NOT_POSTED.includes(row.state))) return null;
  const unsent = rows.filter((row) => NOT_POSTED.includes(row.state)).map((row) => row.workerId);
  const untried = input.taskTotal === undefined || input.taskTotal > rows.length;
  const settled =
    !input.sendActive && !untried && !rows.some((row) => NOT_SETTLED.includes(row.state));
  const who =
    unsent.length > MANY_REFUSED
      ? `${unsent.length} payslips weren’t sent`
      : unsent.length
        ? `${unsent.map((id) => input.names.get(id) ?? 'A worker').join(', ')} ${unsent.length === 1 ? 'wasn’t' : 'weren’t'} sent`
        : '';
  const others = settled ? 'the others are recorded' : 'the others aren’t all confirmed yet';
  return `Sending has started, so checking Xero again can’t change this plan. ${
    who ? `${who}; ${others}` : others.charAt(0).toUpperCase() + others.slice(1)
  }. Contact Statura support.`;
}

/** Page text only, never in a plan's digest. */
export const HELD_WORKER_NEXT_STEP =
  'If you fixed this in Xero, choose Update Xero before sending. If you fixed the employment basis in Statura, build a new version. If you fixed a pay item mapping in Statura, choose Change Xero schedule, then Get Xero ready again; if that isn’t offered, contact Statura support.';

export const isKeyedEarningsLine = (line: {
  numberOfUnits?: number | null;
  normalNumberOfUnits?: number | null;
  fixedAmount?: number | null;
  amount?: number | null;
}): boolean =>
  (line.numberOfUnits ?? line.normalNumberOfUnits ?? 0) !== 0 ||
  (line.fixedAmount ?? 0) !== 0 ||
  (line.amount ?? 0) !== 0;

/** When a wholly refused send was last refused. Null: it wasn't. A date that can't be read allows
 * no replay. */
export function wholeSendRefusedAt(
  attempts: readonly (AttemptFacts & { updatedAt: string })[],
  records: RefusalRecords | null | undefined,
): number | null {
  if (!wholeSendRefused(attempts, records)) return null;
  const times = attempts.map((a) => Date.parse(a.updatedAt));
  return times.some(Number.isNaN) ? Number.POSITIVE_INFINITY : Math.max(...times);
}

/** What the page needs to know about this run's latest send. */
export interface SendFacts {
  status: 'OK' | 'UNAVAILABLE';
  /** Start of the newest send's command. */
  sendStartedAt?: string;
  /** That send can still retry a refused worker. */
  sendActive?: boolean;
  /** That send stopped at the Xero check: Resume can't help, at any refused count. */
  xeroCheckStopped?: boolean;
}
export function sendFactsFrom(
  items: readonly { operation: string; state: string; createdAt: string; reason?: string | null }[],
): SendFacts {
  const newest = items
    .filter((item) => item.operation === 'SEND')
    .reduce<
      (typeof items)[number] | null
    >((best, item) => (!best || Date.parse(item.createdAt) > Date.parse(best.createdAt) ? item : best), null);
  return newest
    ? {
        status: 'OK',
        sendStartedAt: newest.createdAt,
        // Paused for a Xero check can't retry anyone. No reason read: show the worker's line.
        xeroCheckStopped: newest.state === 'PAUSED' && newest.reason === 'XERO_CHECK_REQUIRED',
        sendActive:
          ['QUEUED', 'RUNNING'].includes(newest.state) ||
          (newest.state === 'PAUSED' && newest.reason === 'STEP_UNAVAILABLE'),
      }
    : { status: 'OK' };
}

export const CHECK_ALREADY_DONE_MESSAGE =
  'Statura has already checked Xero for this stop. Contact Statura support.';
/** The newest send or check has stopped at, or for, the Xero check: another check can't start. */
export function xeroCheckAlreadyDone(
  items: readonly {
    operation: string;
    phase?: string;
    state: string;
    createdAt: string;
    reason?: string | null;
  }[],
): boolean {
  const newest = items
    .filter((item) => ['SEND', 'READBACK'].includes(item.operation))
    .reduce<
      (typeof items)[number] | null
    >((best, item) => (!best || Date.parse(item.createdAt) > Date.parse(best.createdAt) ? item : best), null);
  return (
    newest?.state === 'PAUSED' &&
    (newest.operation === 'READBACK' ||
      newest.phase === 'READBACK' ||
      newest.reason === 'XERO_CHECK_REQUIRED')
  );
}

type ReadRow = { workerId: string; outcome: string };
/** A refused worker's draft must read as untouched. A refused worker whose draft matches was keyed
 * by hand, so it doesn't count. */
function readShowsOnlyRefusals(workers: readonly ReadRow[], refused: ReadonlySet<string>) {
  return (
    [...refused].every((id) => workers.some((w) => w.workerId === id)) &&
    workers.every((w) => w.outcome === (refused.has(w.workerId) ? 'ABSENT' : 'MATCHES_INTENT'))
  );
}
/** A finished read where the only workers not matching were refused, and their drafts are as they
 * were. Part of the run went: all refused is its own case, and any other doubt stays doubt. */
export function sentExceptRefused(
  workers: readonly ReadRow[],
  attempts: readonly AttemptFacts[],
): boolean {
  if (refusedStopKind(attempts).kind !== 'PART_OF_RUN') return false;
  const refused = new Set(refusedStopKind(attempts).refused);
  return readShowsOnlyRefusals(workers, refused);
}

/** A finished read where every worker was refused and each draft is as it was. */
export function wholeRunRefusedOnRead(
  workers: readonly ReadRow[],
  attempts: readonly AttemptFacts[],
): boolean {
  if (refusedStopKind(attempts).kind !== 'WHOLE_RUN') return false;
  const refused = new Set(refusedStopKind(attempts).refused);
  return readShowsOnlyRefusals(workers, refused);
}
